import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ResultDialogShell, {
  SectionHeader,
  CompetencyBadges,
} from '../search/ResultDialogShell';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';
import type { ProjectResult } from '../../types/api';

interface ResultDetailDialogProps {
  result: ProjectResult | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ResultDetailDialog({ result, open, onOpenChange }: ResultDetailDialogProps) {
  const { t } = useTranslation('projects');

  if (!result) return null;

  const matchScore =
    typeof result.similarity === 'number' ? result.similarity.toFixed(2) : '—';
  const description = result.description?.trim();

  const header = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <DialogTitle className="text-xl leading-tight">{result.name}</DialogTitle>
        </div>
        <Button
          size="sm"
          variant="default"
          asChild
          title={t('dialog.viewDetailTooltip')}
        >
          <a
            href={`/outcome/${result.id}?from=modal`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dialog.viewDetail')}
            <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Card className="shadow-none">
          <CardContent className="px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dialog.matchScore')}
            </div>
            <div className="mt-0.5 text-base font-semibold tabular-nums text-primary">
              {matchScore}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardContent className="px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dialog.year')}
            </div>
            <div className="mt-0.5 text-base font-semibold tabular-nums">
              {result.year || '—'}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardContent className="px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dialog.type')}
            </div>
            <div className="mt-0.5 truncate text-sm font-medium">
              {result.type || '—'}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  return (
    <ResultDialogShell
      open={open}
      onOpenChange={onOpenChange}
      closeLabel={t('dialog.close')}
      header={header}
    >
      {result.competencies && result.competencies.length > 0 && (
        <section className="mb-6">
          <SectionHeader
            label={t('dialog.matchedCompetencies')}
            count={result.competencies.length}
            tooltip={t('dialog.matchedCompetenciesTooltip')}
          />
          <CompetencyBadges items={result.competencies} />
        </section>
      )}

      {description && (
        <section className="mb-6">
          <SectionHeader label={t('dialog.description')} />
          <p className="text-sm leading-relaxed text-foreground">{description}</p>
        </section>
      )}

      {result.authors && result.authors.length > 0 && (
        <section>
          <SectionHeader
            label={t('dialog.authors')}
            count={result.authors.length}
            tooltip={t('dialog.authorsTooltip')}
          />
          <div className="flex flex-col gap-1.5">
            {result.authors.map((person) => {
              const abbr = person.faculty_abbr ?? '';
              const facultyColor = abbr
                ? FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR
                : null;
              return (
                <a
                  key={person.id}
                  href={`/person/${person.id}?from=modal`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 rounded-md border border-ctu-border bg-muted/30 px-3 py-1.5 text-sm font-medium text-ctu-text no-underline transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary"
                >
                  {abbr && facultyColor && (
                    <span
                      className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white"
                      style={{ backgroundColor: facultyColor }}
                    >
                      {abbr}
                    </span>
                  )}
                  <span className="flex-1 truncate">{person.name}</span>
                  <ArrowUpRight
                    className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100"
                    aria-hidden
                  />
                </a>
              );
            })}
          </div>
        </section>
      )}
    </ResultDialogShell>
  );
}
